import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { Plus, Trash2, X, Users } from "lucide-react";
import formatApiError from "../lib/apiError";

const API = process.env.REACT_APP_BACKEND_URL;
const ax = () => ({ withCredentials: true });

const ROLE_META = {
  admin:            { label: "Super Admin",       color: "#3072FF", bg: "#EEF3FF" },
  state_admin:      { label: "State Admin",       color: "#7C3AED", bg: "#F5F3FF" },
  university_admin: { label: "University Admin",  color: "#1A5AE2", bg: "#E8EEFF" },
  college_admin:    { label: "College Admin",     color: "#059669", bg: "#ECFDF5" },
  it_admin:         { label: "IT Admin",          color: "#D97706", bg: "#FFFBEB" },
  support_user:     { label: "Support User",      color: "#0891B2", bg: "#ECFEFF" },
  faculty:          { label: "Faculty",           color: "#BE185D", bg: "#FDF2F8" },
};

// Roles in the order shown in the dropdown
const ROLE_OPTIONS = [
  { value: "admin",            label: "Super Admin",       description: "MediMagic internal" },
  { value: "state_admin",      label: "State Admin",       description: "Minister-level oversight for a state" },
  { value: "university_admin", label: "University Admin",  description: "RGUHS-style, assigned colleges" },
  { value: "college_admin",    label: "College Admin",     description: "Principal / HOD" },
  { value: "it_admin",         label: "IT Admin",          description: "College IT" },
  { value: "support_user",     label: "Support User",      description: "MediMagic logger / responder" },
  { value: "faculty",          label: "Faculty",           description: "Subject-restricted" },
];

const PASSWORD_ROLES = new Set(["admin", "support_user"]);

const EMPTY_FORM = {
  role: "",
  name: "",
  email: "",
  phone_code: "+91",
  phone: "",
  state_id: "",
  university_id: "",
  college_id: "",
  // Faculty-only
  subject_id: "",
  date_of_birth: "",
  gender: "",
};

export default function UsersPage() {
  const [users, setUsers]               = useState([]);
  const [colleges, setColleges]         = useState([]);
  const [states, setStates]             = useState([]);
  const [universities, setUniversities] = useState([]);
  const [subjects, setSubjects]         = useState([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState("");

  const [modal, setModal]               = useState(false);
  const [form, setForm]                 = useState(EMPTY_FORM);
  const [formErr, setFormErr]           = useState("");
  const [saving, setSaving]             = useState(false);

  const [confirmId, setConfirmId]       = useState(null);
  const [deleting, setDeleting]         = useState(false);

  // Sort & filter controls
  const [sortBy, setSortBy]               = useState("name");        // "name" | "email"
  const [filterCollegeDraft, setFilterCollegeDraft] = useState("");  // in-progress selection
  const [filterCollegeId, setFilterCollegeId]       = useState("");  // applied selection

  const displayedUsers = useMemo(() => {
    let list = [...users];
    // Filter by college
    if (filterCollegeId) {
      list = list.filter((u) => u.college_id === filterCollegeId);
    }
    // Sort
    list.sort((a, b) => {
      const va = (a[sortBy] || "").toLowerCase();
      const vb = (b[sortBy] || "").toLowerCase();
      return va.localeCompare(vb);
    });
    return list;
  }, [users, sortBy, filterCollegeId]);

  const onChange = (key) => (e) => {
    const value = e.target.value;
    setForm((f) => {
      const next = { ...f, [key]: value };
      // Reset scope fields if role changed
      if (key === "role") {
        next.state_id = "";
        next.university_id = "";
        next.college_id = "";
        next.subject_id = "";
        next.date_of_birth = "";
        next.gender = "";
      }
      return next;
    });
  };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [uRes, cRes, sRes, univRes, subRes] = await Promise.all([
        axios.get(`${API}/api/users`,        ax()),
        axios.get(`${API}/api/colleges`,     ax()),
        axios.get(`${API}/api/states`,       ax()),
        axios.get(`${API}/api/universities`, ax()),
        axios.get(`${API}/api/subjects`,     ax()),
      ]);
      setUsers(uRes.data);
      setColleges(cRes.data);
      setStates(sRes.data);
      setUniversities(univRes.data);
      setSubjects(subRes.data);
    } catch (e) {
      setError(formatApiError(e, "Failed to load users."));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // What scope field is required for the selected role
  const scopeRequirement = useMemo(() => {
    switch (form.role) {
      case "state_admin":      return "state";
      case "university_admin": return "university";
      case "college_admin":
      case "it_admin":
      case "faculty":          return "college";
      default:                 return null;
    }
  }, [form.role]);

  const openAdd  = () => { setForm(EMPTY_FORM); setFormErr(""); setModal(true); };
  const closeModal = () => { setModal(false); setFormErr(""); };

  const handleSave = async () => {
    if (!form.role)        { setFormErr("Please select a role.");  return; }
    if (!form.name.trim()) { setFormErr("Name is required.");      return; }
    if (!form.email.trim()){ setFormErr("Email is required.");     return; }
    if (scopeRequirement === "state"      && !form.state_id)      { setFormErr("Please select a state.");      return; }
    if (scopeRequirement === "university" && !form.university_id) { setFormErr("Please select a university."); return; }
    if (scopeRequirement === "college"    && !form.college_id)    { setFormErr("Please select a college.");    return; }
    if (form.role === "faculty" && !form.subject_id)             { setFormErr("Please select a subject.");    return; }

    setSaving(true); setFormErr("");
    const payload = {
      role: form.role,
      name: form.name.trim(),
      email: form.email.trim(),
      phone_code: form.phone_code,
      phone: form.phone.trim(),
      state_id:      form.state_id      || null,
      university_id: form.university_id || null,
      college_id:    form.college_id    || null,
      subject_id:    form.subject_id    || null,
      date_of_birth: form.date_of_birth || null,
      gender:        form.gender        || null,
    };
    try {
      await axios.post(`${API}/api/users`, payload, ax());
      closeModal(); fetchAll();
    } catch (e) {
      setFormErr(formatApiError(e, "Something went wrong."));
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/api/users/${confirmId}`, ax());
      setConfirmId(null); fetchAll();
    } catch (e) {
      alert(formatApiError(e, "Could not delete."));
      setConfirmId(null);
    } finally { setDeleting(false); }
  };

  const scopeText = (u) => {
    if (u.role === "state_admin")      return u.state_name      || "—";
    if (u.role === "university_admin") return u.university_name || "—";
    if (["college_admin", "it_admin", "faculty"].includes(u.role)) return u.college_name || "—";
    return "—";
  };
  const scopeLabel = (role) => {
    if (role === "state_admin")      return "State";
    if (role === "university_admin") return "University";
    if (["college_admin", "it_admin", "faculty"].includes(role)) return "College";
    return "";
  };

  return (
    <div className="page-wrap">
      <div className="page-header-row">
        <div>
          <h1 className="page-title">Users</h1>
          <p className="page-sub">All registered users — across MediMagic and partner institutions.</p>
        </div>
        <button className="btn-primary" onClick={openAdd} data-testid="add-user-btn">
          <Plus size={16} /> Add User
        </button>
      </div>

      {loading ? <div className="table-loading">Loading…</div>
       : error  ? <div className="table-error">{error}</div>
       : (
        <div className="data-card">

          {/* ── Sort + Filter bar ─────────────────────────────────────── */}
          <div className="users-toolbar" data-testid="users-toolbar">
            <div className="users-sort-group">
              <span className="toolbar-label">Sort by</span>
              <button
                className={`sort-btn${sortBy === "name" ? " active" : ""}`}
                onClick={() => setSortBy("name")}
                data-testid="sort-name"
              >Name</button>
              <button
                className={`sort-btn${sortBy === "email" ? " active" : ""}`}
                onClick={() => setSortBy("email")}
                data-testid="sort-email"
              >Email</button>
            </div>

            <div className="users-filter-group">
              <span className="toolbar-label">College</span>
              <select
                className="filter-select"
                value={filterCollegeDraft}
                onChange={(e) => setFilterCollegeDraft(e.target.value)}
                data-testid="filter-college-select"
              >
                <option value="">All colleges</option>
                {colleges.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
              <button
                className="btn-apply"
                onClick={() => setFilterCollegeId(filterCollegeDraft)}
                data-testid="filter-apply"
              >Apply</button>
              {filterCollegeId && (
                <button
                  className="btn-clear"
                  onClick={() => { setFilterCollegeId(""); setFilterCollegeDraft(""); }}
                  data-testid="filter-clear"
                >Clear</button>
              )}
            </div>
          </div>

          {displayedUsers.length === 0 ? (
            <div className="empty-state">
              <Users size={32} color="#43E0FF" />
              <p>{filterCollegeId ? "No users found for the selected college." : "No users yet."}</p>
            </div>
          ) : (
            <div className="table-scroll-wrap">
            <table className="data-table" data-testid="users-table">
              <thead>
                <tr>
                  <th style={{ width: 48 }}>#</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th style={{ width: 170 }}>Role</th>
                  <th>Scope</th>
                  <th style={{ width: 110 }}>Login</th>
                  <th style={{ width: 80 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {displayedUsers.map((u, i) => {
                  const meta = ROLE_META[u.role] || { label: u.role, color: "#77777A", bg: "#F7F8FA" };
                  return (
                    <tr key={u.id} data-testid={`user-row-${u.id}`}>
                      <td className="td-num">{i + 1}</td>
                      <td className="td-name">{u.name || <span className="td-empty">—</span>}</td>
                      <td className="td-desc">{u.email}</td>
                      <td>
                        <span className="role-badge" style={{ color: meta.color, background: meta.bg }}>
                          {meta.label}
                        </span>
                      </td>
                      <td className="td-desc">
                        {scopeText(u) === "—" ? (
                          <span className="td-empty">—</span>
                        ) : (
                          <div>
                            <div className="td-person-sub" style={{ marginBottom: 2 }}>{scopeLabel(u.role)}</div>
                            <div className="td-person-name">{scopeText(u)}</div>
                          </div>
                        )}
                      </td>
                      <td>
                        <span className="login-type-badge">
                          {PASSWORD_ROLES.has(u.role) ? "Password" : "OTP"}
                        </span>
                      </td>
                      <td>
                        <div className="action-btns">
                          <button
                            className="action-delete"
                            onClick={() => setConfirmId(u.id)}
                            title="Delete"
                            data-testid={`delete-user-${u.id}`}
                          >
                            <Trash2 size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          )}
        </div>
      )}

      {modal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-box modal-box-md" onClick={(e) => e.stopPropagation()} data-testid="user-modal">
            <div className="modal-head">
              <h2 className="modal-title">Add User</h2>
              <button className="modal-close" onClick={closeModal}><X size={18} /></button>
            </div>
            {formErr && <div className="form-error" data-testid="form-error">{formErr}</div>}
            <div className="modal-body">
              <div className="field">
                <label className="field-label">Role <span className="required">*</span></label>
                <select
                  className="field-input"
                  value={form.role}
                  onChange={onChange("role")}
                  data-testid="user-role-select"
                  autoFocus
                >
                  <option value="">Select a role…</option>
                  {ROLE_OPTIONS.map((r) => (
                    <option key={r.value} value={r.value}>
                      {r.label} — {r.description}
                    </option>
                  ))}
                </select>
              </div>

              <div className="field">
                <label className="field-label">Name <span className="required">*</span></label>
                <input
                  className="field-input"
                  placeholder="Full name"
                  value={form.name}
                  onChange={onChange("name")}
                  maxLength={120}
                  data-testid="user-name-input"
                />
              </div>

              <div className="field-row">
                <div className="field">
                  <label className="field-label">Email <span className="required">*</span></label>
                  <input
                    className="field-input"
                    type="email"
                    placeholder="user@example.com"
                    value={form.email}
                    onChange={onChange("email")}
                    data-testid="user-email-input"
                  />
                </div>
                <div className="field">
                  <label className="field-label">Phone</label>
                  <div className="phone-field">
                    <select
                      className="field-input phone-code-select"
                      value={form.phone_code}
                      onChange={onChange("phone_code")}
                      data-testid="user-phone-code"
                    >
                      <option value="+91">+91</option>
                      <option value="+1">+1</option>
                      <option value="+44">+44</option>
                      <option value="+61">+61</option>
                      <option value="+971">+971</option>
                      <option value="+65">+65</option>
                    </select>
                    <input
                      className="field-input phone-number-input"
                      placeholder="Phone number"
                      value={form.phone}
                      onChange={onChange("phone")}
                      maxLength={20}
                      data-testid="user-phone-number"
                    />
                  </div>
                </div>
              </div>

              {/* Role-specific scope field */}
              {scopeRequirement === "state" && (
                <div className="field">
                  <label className="field-label">State <span className="required">*</span></label>
                  <select
                    className="field-input"
                    value={form.state_id}
                    onChange={onChange("state_id")}
                    data-testid="user-state-select"
                  >
                    <option value="">Select a state…</option>
                    {states.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.country_code ? `${s.country_code} · ` : ""}{s.state_code} — {s.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {scopeRequirement === "university" && (
                <div className="field">
                  <label className="field-label">University <span className="required">*</span></label>
                  <select
                    className="field-input"
                    value={form.university_id}
                    onChange={onChange("university_id")}
                    data-testid="user-university-select"
                  >
                    <option value="">Select a university…</option>
                    {universities.map((u) => (
                      <option key={u.id} value={u.id}>{u.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {scopeRequirement === "college" && (
                <div className="field">
                  <label className="field-label">College <span className="required">*</span></label>
                  <select
                    className="field-input"
                    value={form.college_id}
                    onChange={onChange("college_id")}
                    data-testid="user-college-select"
                  >
                    <option value="">Select a college…</option>
                    {colleges.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {form.role === "faculty" && (
                <>
                  <div className="field">
                    <label className="field-label">Subject <span className="required">*</span></label>
                    <select
                      className="field-input"
                      value={form.subject_id}
                      onChange={onChange("subject_id")}
                      data-testid="user-subject-select"
                    >
                      <option value="">Select a subject…</option>
                      {subjects.map((s) => (
                        <option key={s.id} value={s.id}>{s.code ? `${s.code} — ${s.name}` : s.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="field-row">
                    <div className="field">
                      <label className="field-label">Date of Birth</label>
                      <input
                        type="date"
                        className="field-input"
                        value={form.date_of_birth}
                        onChange={onChange("date_of_birth")}
                        data-testid="user-dob-input"
                      />
                    </div>
                    <div className="field">
                      <label className="field-label">Gender</label>
                      <select
                        className="field-input"
                        value={form.gender}
                        onChange={onChange("gender")}
                        data-testid="user-gender-select"
                      >
                        <option value="">Select…</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                        <option value="others">Others</option>
                      </select>
                    </div>
                  </div>
                </>
              )}

              {form.role && (
                <p className="field-hint" style={{ color: "#475569" }}>
                  Login type:{" "}
                  <strong>{PASSWORD_ROLES.has(form.role) ? "Password (Welcome@123)" : "OTP (4-digit, hardcoded 0000 for now)"}</strong>
                </p>
              )}
            </div>
            <div className="modal-foot">
              <button className="btn-ghost" onClick={closeModal}>Cancel</button>
              <button className="btn-primary" onClick={handleSave} disabled={saving} data-testid="save-user-btn">
                {saving ? <span className="btn-spinner" /> : "Add User"}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmId && (
        <div className="modal-overlay" onClick={() => setConfirmId(null)}>
          <div className="modal-box modal-box-sm" onClick={(e) => e.stopPropagation()} data-testid="delete-confirm-modal">
            <div className="modal-head">
              <h2 className="modal-title">Delete User</h2>
              <button className="modal-close" onClick={() => setConfirmId(null)}><X size={18} /></button>
            </div>
            <div className="modal-body">
              <p className="confirm-text">
                Are you sure you want to delete this user? This action cannot be undone.
              </p>
            </div>
            <div className="modal-foot">
              <button className="btn-ghost" onClick={() => setConfirmId(null)}>Cancel</button>
              <button className="btn-danger" onClick={handleDelete} disabled={deleting} data-testid="confirm-delete-btn">
                {deleting ? <span className="btn-spinner" /> : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
